import getCart from '../lib/getCart';
import updateCartCount from '../lib/updateCartCount';
import renderMinicart from '../lib/renderMinicart';
import { SELECTORS } from '../utils/constants';

const initQuickBuyDrawer = () => {
  const triggers = document.querySelectorAll('[data-quick-buy]');
  if (!triggers.length) return;

  const drawer = document.querySelector('[data-quick-buy-drawer]');
  if (!drawer) return;

  const overlay = drawer.querySelector('[data-quick-buy-overlay]');
  const panel = drawer.querySelector('[data-quick-buy-panel]');
  const closeBtn = drawer.querySelector('[data-quick-buy-close]');
  const variantsContainer = drawer.querySelector('[data-quick-buy-variants]');
  const headerImage = drawer.querySelector('[data-quick-buy-header-image]');
  const headerTitle = drawer.querySelector('[data-quick-buy-header-title]');
  const headerPrice = drawer.querySelector('[data-quick-buy-header-price]');
  const viewProductLink = drawer.querySelector('[data-quick-buy-view-product]');
  const addToBasketBtn = drawer.querySelector('[data-quick-buy-add-to-basket]');
  const spinner = addToBasketBtn.querySelector(SELECTORS.LOADING_SPINNER);

  const POINTER_EVENTS_NONE = 'pointer-events-none';
  const selectedVariants = new Set();
  let isOpen = false;

  const setDrawerState = (open) => {
    isOpen = open;
    drawer.setAttribute('aria-hidden', !open);
    drawer.classList.toggle(POINTER_EVENTS_NONE, !open);
    overlay.classList.toggle('opacity-0', !open);
    overlay.classList.toggle('opacity-100', open);
    panel.classList.toggle('translate-x-full', !open);
    panel.classList.toggle('translate-x-0', open);
    document.body.classList.toggle('overflow-hidden', open);
    if (open) closeBtn.focus();
    if (!open) selectedVariants.clear();
  };

  const openDrawer = () => setDrawerState(true);
  const closeDrawer = () => setDrawerState(false);

  const updateAddButton = () => {
    addToBasketBtn.disabled = selectedVariants.size === 0;
  };

  const selectVariant = (item, variantId) => {
    const img = item.querySelector('[data-variant-image]');
    const selected = selectedVariants.has(variantId);
    selectedVariants[selected ? 'delete' : 'add'](variantId);
    item.classList.toggle('bg-lighthouse', !selected);
    img.classList.toggle('border', !selected);
    img.classList.toggle('border-black', !selected);
    updateAddButton();
  };

  const getDescText = (raw) => {
    if (!raw) return '';
    if (typeof raw === 'string') return raw.trim();
    if (Array.isArray(raw)) return (raw[0] || '').trim();
    // Shopify rich text object — recursively extract text nodes
    if (typeof raw === 'object') {
      const extract = (node) => {
        if (node.type === 'text') return node.value || '';
        if (node.children) return node.children.map(extract).join('');
        return '';
      };
      return extract(raw).trim();
    }
    return '';
  };

  const renderVariants = (variants) => {
    variantsContainer.innerHTML = '';

    variants.forEach(variant => {
      const desc = getDescText(variant.short_description);

      const item = document.createElement('div');
      item.className = [
        'flex gap-3 items-center px-3 py-1',
        'transition-colors duration-200',
        'hover:bg-lighthouse',
        variant.available ? 'cursor-pointer' : `opacity-50 cursor-not-allowed ${POINTER_EVENTS_NONE}`
      ].join(' ');
      item.setAttribute('data-variant-item', '');
      item.setAttribute('data-variant-id', variant.id);
      item.setAttribute('data-selected', 'false');

      item.innerHTML = `
        <div class="w-10 h-10 shrink-0 overflow-hidden bg-lighthouse">
          <img
            data-variant-image
            src="${variant.image}"
            alt="${variant.title}"
            loading="lazy"
            class="w-full h-full object-cover"
          />
        </div>
        <div class="flex-1 min-w-0 pt-1">
          <p class="text-sm font-medium leading-snug">${variant.title}</p>
          ${desc ? `<p class="text-xs text-black font-thin mt-1 leading-relaxed">${desc}</p>` : ''}
        </div>
      `;

      if (variant.available) {
        item.addEventListener('click', () => selectVariant(item, variant.id));
      }

      variantsContainer.appendChild(item);
    });
  };

  const populateDrawer = (productData, trigger) => {
    headerImage.src = productData.image;
    headerImage.alt = productData.title;
    headerTitle.textContent = productData.title;
    headerPrice.innerHTML = trigger.getAttribute('data-quick-buy-price') || '';
    headerPrice.querySelectorAll('.flex-row-reverse').forEach(el => el.classList.remove('flex-row-reverse'));

    viewProductLink.href = productData.url;
    renderVariants(productData.variants);
    selectedVariants.clear();
    updateAddButton();
  };

  const addMultipleToCart = async () => {
    if (selectedVariants.size === 0) return;

    const items = Array.from(selectedVariants).map(id => ({ id: parseInt(id, 10), quantity: 1 }));
    spinner.classList.remove('hidden');
    addToBasketBtn.classList.add(POINTER_EVENTS_NONE, 'opacity-60');

    try {
      await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      const cart = await getCart();
      updateCartCount(cart.item_count);
      await renderMinicart();
      closeDrawer();
      window.theme?.minicart?.open();
    } catch (err) {
      console.error('Quick buy: add to cart error', err);
    } finally {
      spinner.classList.add('hidden');
      addToBasketBtn.classList.remove(POINTER_EVENTS_NONE, 'opacity-60');
    }
  };

  overlay.addEventListener('click', closeDrawer);
  closeBtn.addEventListener('click', closeDrawer);
  addToBasketBtn.addEventListener('click', addMultipleToCart);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) closeDrawer();
  });

  triggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rawData = trigger.getAttribute('data-quick-buy-product');
      if (!rawData) return;
      try {
        const productData = JSON.parse(rawData);
        populateDrawer(productData, trigger);
        openDrawer();
      } catch (err) {
        console.error('Quick buy: failed to parse product data', err);
      }
    });
  });
};

export default initQuickBuyDrawer;
